import React from 'react';
import { Button, Card, Col, Popconfirm, Row, Space, Tag } from 'antd';
import { DeleteOutlined, EditOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { MonthlyType, Schedule, ScheduleType } from '../types';
import { useUser } from '@sezenta/connection';
import { Permission } from '../../../dto';

interface ScheduleCardProps {
  schedule: Schedule;
  onEdit: (schedule: Schedule) => void;
  onDelete: (scheduleId: string) => void;
  deleting: string | null;
  submitting: boolean;
}

const ScheduleCard: React.FC<ScheduleCardProps> = ({
  schedule,
  onEdit,
  onDelete,
  deleting,
  submitting,
}) => {
  const user = useUser();
  const getScheduleTypeColor = (type: string) => {
    switch (type) {
      case 'Weekly':
        return '#52c41a';
      case 'BiWeekly':
        return '#1890ff';
      case 'Monthly':
        return '#722ed1';
      default:
        return '#d9d9d9';
    }
  };

  const getDayOfWeekColor = (day: string) => {
    const colors = {
      Monday: '#f50',
      Tuesday: '#2db7f5',
      Wednesday: '#87d068',
      Thursday: '#108ee9',
      Friday: '#f56a00',
      Saturday: '#eb2f96',
      Sunday: '#722ed1',
    };
    return colors[day as keyof typeof colors] || '#d9d9d9';
  };

  const getOrdinalSuffix = (num: number): string => {
    const j = num % 10;
    const k = num % 100;
    if (j === 1 && k !== 11) {
      return num + 'st';
    }
    if (j === 2 && k !== 12) {
      return num + 'nd';
    }
    if (j === 3 && k !== 13) {
      return num + 'rd';
    }
    return num + 'th';
  };

  const renderCollectionConfig = () => {
    const { scheduleType, collectionConfig } = schedule;

    switch (scheduleType) {
      case ScheduleType.WEEKLY: {
        const weeklyConfig = collectionConfig.weekly;
        if (!weeklyConfig) return null;

        return (
          <div>
            <div style={{ fontSize: '12px', color: '#666', marginBottom: 8 }}>
              Collection Days
            </div>
            <div>
              {weeklyConfig.days.map((day) => (
                <Tag
                  key={day}
                  color={getDayOfWeekColor(day)}
                  style={{ marginBottom: 4 }}
                >
                  {day.substring(0, 3)}
                </Tag>
              ))}
            </div>
          </div>
        );
      }

      case ScheduleType.BIWEEKLY: {
        const biWeeklyConfig = collectionConfig.biWeekly;
        if (!biWeeklyConfig) return null;

        return (
          <div>
            <div style={{ fontSize: '12px', color: '#666', marginBottom: 8 }}>
              Collection Days
            </div>
            <div style={{ marginBottom: 8 }}>
              <div style={{ fontSize: '11px', color: '#999', marginBottom: 4 }}>
                Week 1:
              </div>
              <div>
                {biWeeklyConfig.week1Days.map((day) => (
                  <Tag
                    key={`w1-${day}`}
                    color={getDayOfWeekColor(day)}
                    style={{ marginBottom: 4 }}
                  >
                    {day.substring(0, 3)}
                  </Tag>
                ))}
              </div>
            </div>
            <div>
              <div style={{ fontSize: '11px', color: '#999', marginBottom: 4 }}>
                Week 2:
              </div>
              <div>
                {biWeeklyConfig.week2Days.map((day) => (
                  <Tag
                    key={`w2-${day}`}
                    color={getDayOfWeekColor(day)}
                    style={{ marginBottom: 4 }}
                  >
                    {day.substring(0, 3)}
                  </Tag>
                ))}
              </div>
            </div>
          </div>
        );
      }

      case ScheduleType.MONTHLY: {
        const monthlyConfig = collectionConfig.monthly;
        if (!monthlyConfig) return null;

        return (
          <div>
            <div style={{ fontSize: '12px', color: '#666', marginBottom: 8 }}>
              Collection Schedule
            </div>
            <div>
              {monthlyConfig.type === MonthlyType.ON_DATE ? (
                <Tag color="blue" style={{ marginBottom: 4 }}>
                  {monthlyConfig.date}
                  {getOrdinalSuffix(monthlyConfig.date)} of each month
                </Tag>
              ) : (
                <Tag color="purple" style={{ marginBottom: 4 }}>
                  {monthlyConfig.ordinal} {monthlyConfig.day} of each month
                </Tag>
              )}
            </div>
          </div>
        );
      }

      default:
        return null;
    }
  };

  return (
    <Card
      size="small"
      style={{
        marginBottom: 16,
        borderLeft: `4px solid ${getScheduleTypeColor(schedule.scheduleType)}`,
      }}
      title={
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Space>
            <Tag color={getScheduleTypeColor(schedule.scheduleType)}>
              {schedule.scheduleType === ScheduleType.BIWEEKLY
                ? 'Fortnightly'
                : schedule.scheduleType}
            </Tag>
            <Tag color={schedule.isActive ? 'green' : 'red'}>
              {schedule.isActive ? 'Active' : 'Inactive'}
            </Tag>
          </Space>
          {user?.permissions.includes(Permission.EDIT_SITE_SCHEDULE) && (
            <Space>
              <Button
                icon={<EditOutlined />}
                size="small"
                onClick={() => onEdit(schedule)}
                type="text"
                disabled={deleting === schedule.id || submitting}
              />
              <Popconfirm
                title="Are you sure you want to delete this schedule?"
                onConfirm={() => onDelete(schedule.id)}
                okText="Yes"
                cancelText="No"
                okButtonProps={{ loading: deleting === schedule.id }}
              >
                <Button
                  icon={<DeleteOutlined />}
                  size="small"
                  danger
                  type="text"
                  loading={deleting === schedule.id}
                  disabled={deleting === schedule.id}
                />
              </Popconfirm>
            </Space>
          )}
        </div>
      }
    >
      <div style={{ marginBottom: 12 }}>
        <Row gutter={16}>
          <Col span={12}>
            <div style={{ fontSize: '12px', color: '#666', marginBottom: 4 }}>
              Start Date
            </div>
            <div style={{ fontWeight: 500 }}>
              {schedule.startDate
                ? dayjs(schedule.startDate).format('DD/MM/YYYY')
                : 'N/A'}
            </div>
          </Col>
          <Col span={12}>
            <div style={{ fontSize: '12px', color: '#666', marginBottom: 4 }}>
              End Date
            </div>
            <div style={{ fontWeight: 500 }}>
              {schedule.endDate
                ? dayjs(schedule.endDate).format('DD/MM/YYYY')
                : 'N/A'}
            </div>
          </Col>
        </Row>
      </div>

      <div style={{ marginBottom: 12 }}>{renderCollectionConfig()}</div>

      {schedule.description && (
        <div>
          <div style={{ fontSize: '12px', color: '#666', marginBottom: 4 }}>
            Description
          </div>
          <div style={{ fontSize: '13px', color: '#666' }}>
            {schedule.description}
          </div>
        </div>
      )}
    </Card>
  );
};

export default ScheduleCard;
